import { resolveFillSettings } from "./fillSettings";
import { runningStitchAttributes } from "./runningStitchAttributes";
import {
  measureSatinColumnWidthMm,
  satinUnderlayAttributes,
  svgMillimetersPerUnit,
} from "./satinUnderlay";
/**
 * Injects Ink/Stitch embroidery attributes per-layer into an SVG JSON tree.
 * Supports multi-pass layers: a single layer can have fill + running passes
 * that both get serialized onto the same SVG elements.
 *
 * @param {object} svgJson   – svgson JSON object (the full <svg>)
 * @param {object} layerStitchSettings – { [layerIndex]: { passes: [...], activePassIndex } }
 * @param {object} layerRails – { [layerIndex]: { pairs: [{ rail0: "M…", rail1: "M…" }, ...] } }
 * @returns {object} mutated svgJson with attributes injected
 */
// Default amount a fill grows beneath an adjacent satin border so the two
// layers overlap. A satin column's footprint is fixed by its rails (it has no
// "expand"), so the only way to close the gap that opens when stitches pull the
// fabric in is to let the fills underneath bleed slightly under the satin.
// Kept to the standard fill expand (0.16 mm): a larger value is applied as a
// blanket expand to *every* fill when the design contains satin, which on fine,
// gap-heavy designs (e.g. the spaces between hair strands or rays) inflates the
// fills enough to swallow the negative space and read as a solid blob. 0.16 mm
// still underlaps the seam without closing intended gaps.
export const SATIN_UNDERLAP_MM = 0.16;

// Default pull compensation for satin columns. Satin has no "expand" — pull
// compensation is the only lever that grows a column past its rails, so giving
// every satin a small default makes it overlap its neighbours (fabric, fill, or
// an adjacent satin) instead of leaving a gap when the stitches pull in.
export const SATIN_PULL_COMP_MM = 0.2;

export default function injectEmbroideryAttributes(
  svgJson,
  layerStitchSettings = {},
  layerRails = {}
) {
  if (!svgJson?.children) return svgJson;

  const clone = JSON.parse(JSON.stringify(svgJson));
  clone.attributes = clone.attributes || {};
  clone.attributes["xmlns:inkstitch"] = "http://inkstitch.org/namespace";

  // Detect whether the design contains any satin layer. When it does, fills get
  // a small default underlap (below) so they tuck under the satin and don't
  // leave a ring of bare fabric where the colors meet.
  const designHasSatin = clone.children.some((_child, i) => {
    const ls = layerStitchSettings[i];
    const passes = ls?.passes || (ls ? [ls] : []);
    if (passes.some((p) => (p?.stitchType || p?.fill_method) === "satin"))
      return true;
    const re = layerRails[i];
    const rp = re?.pairs || [];
    if (rp.some((p) => p.rail0 && p.rail1)) return true;
    return !!(re?.rail0 && re?.rail1);
  });

  const mmPerUnit = svgMillimetersPerUnit(clone);
  clone.children.forEach((child, i) => {
    if (!child.attributes) child.attributes = {};

    // A layer the user never explicitly configured must still stitch — falling
    // through here used to `return` and drop the layer from the output entirely,
    // which is why some layers (and freshly added sublayers) went missing on
    // generate. Default unconfigured layers to a plain auto-fill instead.
    const layerSettings = layerStitchSettings[i];
    const passes =
      layerSettings?.passes ||
      (layerSettings
        ? [layerSettings]
        : [{ stitchType: "auto_fill", fill_method: "auto_fill" }]);
    const railEntry = layerRails[i];
    const railPairs = railEntry?.pairs || [];
    let completePairs = railPairs.filter((p) => p.rail0 && p.rail1);
    if (completePairs.length === 0 && railEntry?.rail0 && railEntry?.rail1) {
      completePairs = [{ rail0: railEntry.rail0, rail1: railEntry.rail1 }];
    }

    for (const pass of passes) {
      const st = pass.stitchType || "auto_fill";
      switch (st) {
        case "satin":
          if (completePairs.length <= 1) {
            injectSatin(child, pass, completePairs[0] || null, mmPerUnit);
          } else {
            child.name = "g";
            child.children = completePairs.map((pair) => {
              const satinEl = {
                name: "path",
                type: "element",
                attributes: { ...(child.attributes || {}) },
                children: [],
              };
              injectSatin(satinEl, pass, pair, mmPerUnit);
              return satinEl;
            });
          }
          break;
        case "running":
          injectRunning(child, pass);
          break;
        case "auto_fill":
        default:
          injectFill(child, withSatinUnderlap(resolveFillSettings(pass), designHasSatin));
          break;
      }
    }
  });

  return clone;
}

// Give a fill a small outward expand so it underlaps the satin border, unless
// the user already chose their own expand value (explicit choice always wins).
function withSatinUnderlap(pass, designHasSatin) {
  if (!designHasSatin) return pass;
  if (pass?.expand_mm != null) return pass;
  return { ...pass, expand_mm: SATIN_UNDERLAP_MM };
}

function injectFill(el, input) {
  const s = resolveFillSettings(input);
  el.attributes["inkstitch:fill_method"] = s.fill_method || s.stitchType || "auto_fill";
  const numeric = ['angle', 'row_spacing_mm', 'max_stitch_length_mm', 'pull_compensation_mm',
    'expand_mm', 'staggers', 'fill_underlay_row_spacing_mm', 'fill_underlay_angle',
    'fill_underlay_inset_mm', 'fill_underlay_max_stitch_length_mm', 'random_stitch_length_jitter_percent'];
  for (const key of numeric) {
    if (s[key] != null) el.attributes[`inkstitch:${key}`] = String(s[key]);
    else delete el.attributes[`inkstitch:${key}`];
  }
  el.attributes["inkstitch:fill_underlay"] = s.fill_underlay !== false ? "true" : "false";
  el.attributes["inkstitch:enable_random_stitch_length"] = s.randomize_stitch_length ? "true" : "false";
  applyToChildren(el, s, injectFill);
}

function injectSatin(el, s, rails, mmPerUnit) {
  el.attributes["inkstitch:satin_column"] = "true";
  if (s.zigzag_spacing_mm)
    el.attributes["inkstitch:zigzag_spacing_mm"] = String(s.zigzag_spacing_mm);
  const satinPull =
    s.pull_compensation_mm != null
      ? s.pull_compensation_mm
      : SATIN_PULL_COMP_MM;
  el.attributes["inkstitch:pull_compensation_mm"] = String(satinPull);
  if (s.push_compensation_mm != null)
    el.attributes["inkstitch:push_compensation_mm"] = String(
      s.push_compensation_mm
    );
  if (s.max_stitch_length_mm)
    el.attributes["inkstitch:max_stitch_length_mm"] = String(
      s.max_stitch_length_mm
    );
  if (s.split_method) el.attributes["inkstitch:split_method"] = s.split_method;
  if (s.split_method === "staggered" && s.split_staggers) {
    el.attributes["inkstitch:split_staggers"] = String(s.split_staggers);
  }
  if (s.short_stitch_distance_mm != null) {
    el.attributes["inkstitch:short_stitch_distance_mm"] = String(
      s.short_stitch_distance_mm
    );
    el.attributes["inkstitch:short_stitch_inset"] = String(
      s.short_stitch_inset ?? 15
    );
  }
  if (s.reverse_rails)
    el.attributes["inkstitch:reverse_rails"] = s.reverse_rails;
  el.attributes["inkstitch:swap_satin_rails"] = s.swap_satin_rails
    ? "true"
    : "false";

  const widthMm = measureSatinColumnWidthMm(
    rails?.rail0,
    rails?.rail1,
    mmPerUnit
  );
  Object.assign(el.attributes, satinUnderlayAttributes(s, widthMm));

  if (rails?.rail0 && rails?.rail1) {
    const rungPaths = (rails.rungs || []).join(" ");
    el.attributes.d =
      rails.rail0 + " " + rails.rail1 + (rungPaths ? " " + rungPaths : "");
    el.attributes.stroke = el.attributes.fill || "#000";
    el.attributes["stroke-width"] = "1";
    el.attributes.fill = "none";
  }

  applyToChildren(el, s, (child, cs) =>
    injectSatin(child, cs, rails, mmPerUnit)
  );
}

function injectRunning(el, s, inheritedColor) {
  const color = runningStitchAttributes(el.attributes, inheritedColor);
  el.attributes["inkstitch:stroke_method"] = "running_stitch";
  if (s.running_stitch_length_mm)
    el.attributes["inkstitch:running_stitch_length_mm"] = String(
      s.running_stitch_length_mm
    );
  if (s.bean_stitch_repeats)
    el.attributes["inkstitch:bean_stitch_repeats"] = String(
      s.bean_stitch_repeats
    );
  if (s.randomize_stitch_length)
    el.attributes["inkstitch:randomize_stitch_length"] = "true";
  if (s.pull_compensation_mm)
    el.attributes["inkstitch:pull_compensation_mm"] = String(
      s.pull_compensation_mm
    );

  for (const child of el.children || []) {
    child.attributes = child.attributes || {};
    injectRunning(child, s, color);
  }
}

function applyToChildren(el, s, fn) {
  if (el.children && Array.isArray(el.children)) {
    el.children.forEach((child) => {
      if (child.attributes) fn(child, s);
    });
  }
}
